import { cacheClear } from './cache';

const TOKEN_KEY = 'recipease_token';

export function getToken(): string | null {
	try {
		return window.localStorage.getItem(TOKEN_KEY);
	} catch {
		return null;
	}
}

export function setToken(token: string): void {
	// A new session must not inherit the previous one's cached responses
	cacheClear();
	try {
		window.localStorage.setItem(TOKEN_KEY, token);
	} catch {
		// storage unavailable (private browsing etc.) — session lives in memory only
	}
}

export function clearToken(): void {
	cacheClear();
	try {
		window.localStorage.removeItem(TOKEN_KEY);
	} catch {
		// ignore
	}
}

export class ApiError extends Error {
	status: number;
	code?: string;

	constructor(message: string, status: number, code?: string) {
		super(message);
		this.name = 'ApiError';
		this.status = status;
		this.code = code;
	}
}

export function isPlanLimitError(error: unknown): boolean {
	return error instanceof ApiError && error.code === 'plan_limit';
}

const baseUrl = (): string => import.meta.env.VITE_API_URL || '';

/**
 * Absolute URL to give AI assistants for the MCP server. VITE_MCP_URL wins
 * when set (production serves it at /mcp on the web app's own domain, proxied
 * to the API by vercel.json); otherwise it's the API's own /mcp endpoint.
 */
export function mcpEndpoint(): string {
	const configured = import.meta.env.VITE_MCP_URL;
	if (configured) return configured;
	return new URL(`${baseUrl()}/mcp`, window.location.origin).toString();
}

/**
 * Fetch wrapper for the Recipease API: attaches the JWT, sets JSON headers,
 * and turns non-2xx responses into typed ApiErrors (previous code silently
 * parsed error payloads as success).
 */
export async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
	const headers: Record<string, string> = { ...(options.headers as Record<string, string>) };
	if (options.body !== undefined && !(options.body instanceof FormData)) {
		headers['Content-Type'] = 'application/json';
	}
	const token = getToken();
	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}

	const response = await fetch(`${baseUrl()}${path}`, { ...options, headers });

	if (response.status === 204) {
		return undefined as T;
	}

	let body: unknown = null;
	try {
		body = await response.json();
	} catch {
		// non-JSON body; handled below
	}

	if (!response.ok) {
		const errorBody = body as { error?: { message?: string; code?: string } } | null;
		throw new ApiError(
			errorBody?.error?.message || `Request failed (${response.status})`,
			response.status,
			errorBody?.error?.code
		);
	}

	return body as T;
}
